import { AppHeader } from '@/components/layout/app-header'
import { AppSidebar } from '@/components/layout/app-sidebar'
import { SidebarProvider } from '@/components/ui/sidebar'
import { AUTH } from '@/constants/auth'
import { AudioNotificationProvider } from '@/context/audio-notification-context'
import { SearchProvider } from '@/context/search-context'
import { SocketProvider } from '@/context/socket-context'
import { AuthenticatedChatRealtimeListener } from '@/features/chats/components/authenticated-chat-realtime-listener'
import { cn } from '@/lib/utils'
import { listTeamsQuery, teamPermissionsQuery } from '@/queries/team.query'
import { TeamService } from '@/services/team.service'
import { useAuthStore } from '@/stores/auth.store'
import { usePermissionsStore } from '@/stores/permissions.store'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Outlet } from '@tanstack/react-router'
import Cookies from 'js-cookie'
import { useEffect } from 'react'

export const Route = createFileRoute('/_authenticated')({
  component: RouteComponent,
  loader: async ({ context }) => {
    await context.queryClient.ensureQueryData(listTeamsQuery)
    try {
      await context.queryClient.ensureQueryData(teamPermissionsQuery)
    } catch {
      context.queryClient.setQueryData(['team-permissions'], null)
    }
  },
  async beforeLoad() {
    const authStore = useAuthStore.getState()
    if (authStore.activeTeam) return
    const teams = await TeamService.getTeams()
    const teamId = Cookies.get(AUTH.TEAM_ID)
    if (teamId) {
      const team = teams.find((team) => team.id === +teamId)
      if (team) {
        authStore.setActiveTeam(team)
        return
      }
    }
    authStore.setActiveTeam(teams[0])
  },
})

function RouteComponent() {
  const defaultOpen = Cookies.get('sidebar:state') !== 'false'
  const setPermissions = usePermissionsStore((state) => state.setPermissions)
  const setLoading = usePermissionsStore((state) => state.setLoading)
  const resetToDefault = usePermissionsStore((state) => state.resetToDefault)
  const getHasFullTeamAccess = useAuthStore(
    (state) => state.getHasFullTeamAccess
  )

  const { data: permissions, isLoading, isError } = useQuery(teamPermissionsQuery)

  useEffect(() => {
    setLoading(isLoading)

    if (isError || permissions === null) {
      const hasFullTeamAccess = getHasFullTeamAccess()
      if (!hasFullTeamAccess) {
        resetToDefault()
      }
      setLoading(false)
      return
    }

    if (permissions) {
      setPermissions(permissions)
    }
  }, [
    permissions,
    isLoading,
    isError,
    setPermissions,
    setLoading,
    resetToDefault,
    getHasFullTeamAccess,
  ])

  return (
    <SocketProvider>
      <AudioNotificationProvider>
        <AuthenticatedChatRealtimeListener />
        <SearchProvider>
          <SidebarProvider defaultOpen={defaultOpen}>
            <AppSidebar />
            <div
              id='content'
              className={cn(
                'ml-auto w-full max-w-full',
                'peer-data-[state=collapsed]:w-[calc(100%-var(--sidebar-width-icon)-1rem)]',
                'peer-data-[state=expanded]:w-[calc(100%-var(--sidebar-width))]',
                'transition-[width] duration-200 ease-linear',
                'flex h-svh flex-col',
                'group-data-[scroll-locked=1]/body:h-full',
                'group-data-[scroll-locked=1]/body:has-[main.fixed-main]:h-svh'
              )}
            >
              <AppHeader />
              <Outlet />
            </div>
          </SidebarProvider>
        </SearchProvider>
      </AudioNotificationProvider>
    </SocketProvider>
  )
}
